import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Store, Eye, ShoppingBag, Package, DollarSign, ImagePlus, X } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { uploadImage } from "../lib/media";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API_BASE = process.env.REACT_APP_API_URL;
const EMPTY = { title: "", category: "", price_min: "", price_max: "", moq: 1, stock: "", description: "" };

function ProductFormDialog({ open, onOpenChange, cats, initial, onSave, saving, stage }) {
  const [form, setForm] = useState(EMPTY);
  const [kept, setKept] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
      setKept(initial?.images || []);
      setFiles([]);
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    const room = Math.max(0, 5 - kept.length - files.length);
    const next = picked.slice(0, room).map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
    if (picked.length > room) toast.error("Maximum 5 photos per product");
    setFiles([...files, ...next]);
    e.target.value = "";
  };

  const submit = (e) => {
    e.preventDefault();
    onSave(form, { keptIds: kept.map((i) => i.id), files: files.map((f) => f.file) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial ? "Edit product" : "Add a product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Photos ({kept.length + files.length}/5)</label>
            <div className="mt-2 flex flex-wrap gap-3">
              {kept.map((img) => (
                <div key={img.id} className="relative h-20 w-20 overflow-hidden rounded-xl border border-slate-200">
                  <img src={mediaUrl(img.thumb || img.url)} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    data-testid={`photo-remove-${img.id}`}
                    onClick={() => setKept(kept.filter((k) => k.id !== img.id))}
                    className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-red-600"
                    aria-label="Remove photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {files.map((f, i) => (
                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-teal-300">
                  <img src={f.preview} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    data-testid={`photo-new-remove-${i}`}
                    onClick={() => setFiles(files.filter((_, x) => x !== i))}
                    className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-red-600"
                    aria-label="Remove photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {kept.length + files.length < 5 && (
                <label
                  data-testid="product-form-photo-input"
                  className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-teal-300 text-teal-600 hover:bg-teal-50 transition"
                >
                  <ImagePlus className="h-5 w-5" />
                  <span className="mt-1 text-[10px] font-semibold">Add photo</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={addFiles} />
                </label>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-400">JPG or PNG, at least 100×100 px. First photo becomes the cover.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Title</label>
            <input data-testid="product-form-title" required value={form.title} onChange={set("title")} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Category</label>
              <select data-testid="product-form-category" required value={form.category} onChange={set("category")} className={`${inputCls} bg-white`}>
                <option value="">Select...</option>
                {cats.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">MOQ</label>
              <input data-testid="product-form-moq" type="number" min={1} required value={form.moq} onChange={set("moq")} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Min price ($)</label>
              <input data-testid="product-form-price-min" type="number" step="0.01" min={0} value={form.price_min} onChange={set("price_min")} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Max price ($)</label>
              <input data-testid="product-form-price-max" type="number" step="0.01" min={0} value={form.price_max} onChange={set("price_max")} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Stock</label>
              <input data-testid="product-form-stock" type="number" min={0} value={form.stock} onChange={set("stock")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Description</label>
            <textarea data-testid="product-form-description" rows={4} value={form.description} onChange={set("description")} className={inputCls} />
          </div>
          <button
            data-testid="product-form-save"
            type="submit"
            disabled={saving}
            className="w-full rounded-lg bg-teal-700 py-2.5 text-sm font-bold text-white hover:bg-teal-800 transition active:scale-95 disabled:opacity-60"
          >
            {saving ? stage || "Saving..." : initial ? "Save changes" : "Add product"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function SellerDashboard() {
  const { user, isSeller } = useAuth();
  const { t } = useLang();
  const { fmt, fmtRange } = useCurrency();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [cats, setCats] = useState([]);
  const [mediaT, setMediaT] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState("");
  const [becoming, setBecoming] = useState(false);

  const load = () => api("/seller/dashboard", { auth: true }).then(setDash).catch((e) => toast.error(e.message));

  useEffect(() => {
    if (user === null) return;
    if (!user) {
      navigate("/login?next=/seller");
      return;
    }
    if (isSeller) {
      load();
      api("/categories").then((r) => setCats(r.filter((c) => c.enabled))).catch(() => {});
      api("/media/token", { method: "POST", auth: true }).then((r) => setMediaT(r.token)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSeller]);

  if (!user) return null;

  const dashImg = (p) => {
    if (!p) return "";
    if (p.startsWith("http")) return p;
    return `${API_BASE}${p}${mediaT ? `?t=${mediaT}` : ""}`;
  };

  if (!isSeller)
    return (
      <div data-testid="become-seller-page" className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-teal-700/10 text-teal-700">
          <Store className="h-8 w-8" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">{t("become_t")}</h1>
        <p className="mt-3 text-sm sm:text-base text-slate-600">{t("become_sub")}</p>
        <button
          data-testid="become-seller-button"
          disabled={becoming}
          onClick={async () => {
            setBecoming(true);
            try {
              await api("/auth/become-seller", { method: "POST", auth: true });
              toast.success(t("become_btn"));
              navigate("/login?next=/seller");
            } catch (e) {
              toast.error(e.message);
            } finally {
              setBecoming(false);
            }
          }}
          className="mt-6 rounded-full bg-teal-700 px-6 py-3 text-sm font-bold text-white shadow-md shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95 disabled:opacity-60"
        >
          {becoming ? t("becoming") : t("become_btn")}
        </button>
      </div>
    );

  const save = async (form, media) => {
    setSaving(true);
    setStage("");
    const num = (v) => (v === "" || v === null ? null : Number(v));
    try {
      const newIds = [];
      for (let i = 0; i < (media?.files?.length || 0); i++) {
        setStage(`Uploading photo ${i + 1}/${media.files.length}...`);
        const asset = await uploadImage(media.files[i], setStage);
        newIds.push(asset.id);
      }
      const imageIds = [...(media?.keptIds || []), ...newIds];
      setStage("Saving product...");
      let pid = editing?.id;
      if (editing) {
        await api(`/seller/products/${editing.id}`, {
          method: "PATCH",
          auth: true,
          body: {
            title: form.title,
            description: form.description,
            category: form.category,
            moq: Number(form.moq),
            price_min: num(form.price_min),
            price_max: num(form.price_max),
            stock: num(form.stock),
          },
        });
      } else {
        const created = await api("/seller/products", {
          method: "POST",
          auth: true,
          body: {
            title: form.title,
            description: form.description,
            category: form.category,
            moq: Number(form.moq),
            price_min: num(form.price_min),
            price_max: num(form.price_max),
            stock: num(form.stock),
          },
        });
        pid = created.id;
      }
      if (pid && (imageIds.length > 0 || editing)) {
        await api(`/products/${pid}/media`, {
          method: "PUT",
          auth: true,
          body: { images: imageIds, cover_id: imageIds[0] || null },
        });
      }
      toast.success(editing ? "Product updated" : "Product added");
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed", { description: e.message });
    } finally {
      setSaving(false);
      setStage("");
    }
  };

  const toggleStatus = async (p) => {
    try {
      await api(`/seller/products/${p.id}`, {
        method: "PATCH",
        auth: true,
        body: { status: p.status === "active" ? "draft" : "active" },
      });
      toast.success(p.status === "active" ? t("unlist") : t("publish"));
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.title}”? This cannot be undone.`)) return;
    try {
      await api(`/seller/products/${p.id}`, { method: "DELETE", auth: true });
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const kpis = dash?.kpis || {};
  const cards = [
    { icon: Eye, label: t("kpi_views"), value: kpis.views ?? 0, tid: "kpi-views" },
    { icon: ShoppingBag, label: t("kpi_orders"), value: kpis.orders ?? 0, tid: "kpi-orders" },
    { icon: Package, label: t("kpi_products"), value: kpis.products ?? 0, tid: "kpi-products" },
    { icon: DollarSign, label: t("kpi_revenue"), value: fmt(kpis.revenue ?? 0), tid: "kpi-revenue" },
  ];

  return (
    <div data-testid="seller-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase font-bold tracking-[0.2em] text-teal-700">{t("seller_dash")}</p>
          <h1 className="font-heading mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            {dash?.supplier?.name || user.company || user.name}
          </h1>
          {dash?.supplier?.country && <p className="text-sm text-slate-500 mt-0.5">{dash.supplier.country}</p>}
        </div>
        <button
          data-testid="seller-add-product-button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-full bg-teal-700 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95"
        >
          <Plus className="h-4 w-4" /> {t("add_product")}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ icon: Icon, label, value, tid }) => (
          <div key={tid} data-testid={tid} className="rounded-2xl border border-teal-900/10 bg-white p-5">
            <div className="flex items-center gap-2 text-teal-700">
              <Icon className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
            </div>
            <p className="font-heading mt-2 text-2xl font-extrabold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-teal-900/10 bg-white overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="font-heading font-semibold text-slate-900">{t("your_products")}</h2>
        </div>
        {!dash ? (
          <div data-testid="seller-products-loading" className="p-8 text-center text-sm text-slate-500">...</div>
        ) : dash.products.length === 0 ? (
          <div data-testid="seller-products-empty" className="p-10 text-center">
            <p className="font-heading font-semibold text-slate-800">{t("no_products")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("no_products_sub")}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {dash.products.map((p) => (
              <div key={p.id} data-testid={`seller-product-row-${p.id}`} className="flex items-center gap-4 px-5 py-4">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-teal-50">
                  {p.image && <img src={dashImg(p.thumb || p.image)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{p.title}</p>
                  <p className="text-xs text-slate-500">
                    {fmtRange(p)} · MOQ {p.moq} · <span className="capitalize">{p.category}</span>
                  </p>
                </div>
                <span
                  data-testid={`seller-product-status-${p.id}`}
                  className={`hidden sm:inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    p.status === "active" ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {p.status}
                </span>
                <button
                  data-testid={`seller-product-toggle-${p.id}`}
                  onClick={() => toggleStatus(p)}
                  className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal-400 hover:text-teal-700 transition"
                >
                  {p.status === "active" ? t("unlist") : t("publish")}
                </button>
                <button
                  data-testid={`seller-product-edit-${p.id}`}
                  onClick={() => {
                    setEditing(p);
                    setFormOpen(true);
                  }}
                  className="rounded-lg p-2 text-slate-500 hover:bg-teal-50 hover:text-teal-700 transition"
                  aria-label="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  data-testid={`seller-product-delete-${p.id}`}
                  onClick={() => remove(p)}
                  className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 transition"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ProductFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        cats={cats}
        initial={editing}
        onSave={save}
        saving={saving}
        stage={stage}
      />
    </div>
  );
}
